import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';

// Largeur de la carte au-delà du mobile (sm+). Sur mobile, le modal est plein écran
// quelle que soit la taille — donc pas de plafond de largeur.
const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  fullscreen: '',
};

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  size?: ModalSize;
  children: ReactNode;
  /** Contenu du footer (boutons d'action). */
  footer?: ReactNode;
}

/**
 * Modal : header (titre + X), body scrollable, footer d'actions.
 * Tailles sm/md/lg/xl/fullscreen. Brief — composant #5.
 */
export function Modal({ open, onOpenChange, title, description, size = 'md', children, footer }: ModalProps) {
  const isFullscreen = size === 'fullscreen';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden bg-surface-secondary shadow-lg data-[state=open]:animate-slide-up',
            // Mobile : plein écran (inset-0, sans bordure ni arrondi) + marges de
            // sécurité iOS (le titre/le X ne passent jamais sous l'heure ; le footer
            // reste au-dessus de l'indicateur d'accueil). Réinitialisées en sm+.
            'inset-0 w-full max-w-none rounded-none border-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:p-0',
            // sm+ : carte centrée (largeur via SIZE_CLASS).
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:border sm:border-border',
            SIZE_CLASS[size],
            isFullscreen && 'sm:left-0 sm:top-0 sm:max-h-screen sm:w-screen sm:max-w-none sm:translate-x-0 sm:translate-y-0 sm:rounded-none',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-bold text-content-primary">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="mt-0.5 text-sm text-content-secondary">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="rounded-md p-1.5 text-content-muted transition-colors hover:bg-surface-hover hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
              aria-label="Fermer"
            >
              <X className="size-5" />
            </Dialog.Close>
          </div>

          <div className="scrollbar-thin flex-1 overflow-y-auto px-6 py-5">{children}</div>

          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-border bg-surface-primary px-6 py-4">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
